<script setup>
import SettingsLayout from "@/Layouts/Settings.vue";
import Input from "@/Components/Settings/Input.vue";
import Textarea from "@/Components/Textarea.vue";
import Select from "@/Components/Select.vue";
import Label from "@/Components/Settings/Label.vue";
import HeaderLink from "@/Components/Settings/HeaderLink.vue";
import HeaderButton from "@/Components/Settings/HeaderLink.vue";
import Button from "@/Components/Button.vue";
import Heading from "@/Components/Settings/Heading.vue";
import { Head, useForm, Link } from "@inertiajs/inertia-vue3";
import TD from "@/Components/Table/Element.vue";
import { Inertia } from "@inertiajs/inertia";
import { ref, onMounted } from "vue";
const page_name = "Terminimporter";

const canEdit = Inertia.page.props.auth.can["edit settings"];
const canDelete = Inertia.page.props.auth.can["delete settings"];
const obsElement = ref(null);
const allAppointments = ref(props.appointments.data || []);

const props = defineProps({
  errors: Object,
  appointments: Object,
});

onMounted(() => {
  const observer = new IntersectionObserver((entries) =>
    entries.forEach((entry) => entry.isIntersecting && loadMoreAppointments(), {
      rootMargin: "-150px 0px 0px 0px",
    })
  );

  observer.observe(obsElement.value);
});

function deleteAppointment(id) {
  Inertia.delete("/termin-importer/" + id);
}

function loadMoreAppointments() {
  if (
    props.appointments.length === 0 ||
    props.appointments.next_page_url === null
  ) {
    return;
  }

  //console.log(props.appointments);

  Inertia.get(
    props.appointments.next_page_url,
    {},
    {
      preserveState: true,
      preserveScroll: true,
      only: ["appointments"],
      onSuccess: () => {
        allAppointments.value = [
          ...allAppointments.value,
          ...props.appointments.data,
        ];
        window.history.replaceState({}, "Termin-Importer", "/einstellungen/termin-importer");
      },
    }
  );
}

function submitForm() {
  form.post("/importAppointments");
}

const form = useForm({
  file: null,
});
</script>

<template>
  <SettingsLayout>
    <!-- <Head title="Stammdaten - Grundeinstellungen" /> -->
    <Head :title="`${page_name} - Grundeinstellungen`" />

    <template #breadcrumb>{{ page_name }}</template>
    <template #controls>
      <!-- class="mr-3" -->

      <HeaderLink href="/einstellungen/integration" type="close"
        >Zurück</HeaderLink
      >
      <!-- <HeaderButton @click="submitForm()">Speichern</HeaderButton> -->
    </template>
    <Heading title="Terminimport" />

    <p class="max-w-5xl text-sm text-oteoText">
      Spitta-oteo bietet Ihnen die Möglichkeiten bereits vergebene Termine aus
      Ihrem jetzigem Terminplanungssystem zu importieren. Sprechen Sie uns gerne
      an, ob diese Funktion auch für Ihr Terminplanungssystem zur Verfügung
      steht, oder erfragen Sie beim Hersteller Ihres jetzigen
      Terminplanungssystems ob es eine Exportmöglichkeit als CSV-Datei gibt. Die
      CSV-Datei muss UTF-8 codiert sein und es werden folgende Spalten
      erwartet:
    </p>
    <ul class="text-sm text-oteoText">
      patid | anrede | vorname | nachname | termin | start | ende | behandler |
      notiz
    </ul>
    <p class="mt-4 text-sm">
      Beispieldatei zum Downloaden:
      <a class="text-oteo-300" href="/storage/BeispielImport.csv">
        BeispielImport.csv</a
      >
    </p>

    <div class="flex items-center max-w-4xl mt-4 text-sm text-oteoText">
      <div class="flex flex-col relative">
        <input
          type="file"
          accept=".csv"
          @input="form.file = $event.target.files[0]"
        />

        <progress
          class="absolute b-0 mt-4"
          v-if="form.progress"
          :value="form.progress.percentage"
          max="100"
        >
          {{ form.progress.percentage }}%
        </progress>
      </div>

      <div class="">
        <Button
          :disabled="!form.file"
          :class="{
            'opacity-50': !form.file,
            'cursor-not-allowed': !form.file,
          }"
          v-if="canEdit"
          @click="submitForm()"
          class=""
        >
          Importieren
        </Button>
      </div>
    </div>

    <div class="max-h-[350px] mt-12 overflow-y-scroll">
      <table class="min-w-full">
        <thead class="bg-white border-b">
          <tr class="sticky -top-px border-white border-t bg-white">
            <th
              scope="col"
              class="
                text-sm
                font-medium
                text-gray-900
                px-6
                py-4
                text-left
                border-x-2
              "
            >
              Datum
            </th>
            <th
              scope="col"
              class="
                text-sm
                font-medium
                text-gray-900
                px-6
                py-4
                text-left
                border-x-2
              "
            >
              Uhrzeit
            </th>

            <th
              scope="col"
              class="
                text-sm
                font-medium
                text-gray-900
                px-6
                py-4
                text-left
                border-x-2
              "
            >
              Patient
            </th>

            <th
              scope="col"
              class="
                text-sm
                font-medium
                text-gray-900
                px-6
                py-4
                text-left
                border-x-2
              "
            >
              Aktionen
            </th>
          </tr>
        </thead>
        <tbody class="">
          <tr
            v-for="(slot, index) in allAppointments"
            :key="slot.id"
            class="border-b"
            :class="{
              'bg-gray-100': index % 2 === 0,
            }"
          >
            <TD>
              {{ slot.dateString }}
            </TD>
            <TD> {{ `${slot.startString} - ${slot.endString} Uhr` }} </TD>
            <TD> {{ `${slot.firstname} ${slot.lastname}` }} </TD>
            <TD>
              <div v-if="canEdit" class="flex items-center text-oteo-300">
                <Link
                  class="mr-4"
                  :href="`/einstellungen/termin-importer/${slot.id}/erstellen/`"
                >
                  <i class="las la-angle-right"></i>bearbeiten
                </Link>
                <div
                  v-if="canDelete"
                  class="cursor-pointer"
                  @click="deleteAppointment(slot.id)"
                >
                  <i class="las la-times"></i>
                  löschen
                </div>
              </div>
            </TD>
          </tr>
        </tbody>
      </table>
      <div
        class="sticky bottom-0 h-10 w-full bg-gradient-to-t from-white"
      ></div>
      <div class="h-8 w-full bg-transparent" ref="obsElement"></div>
    </div>
  </SettingsLayout>
</template>
